import { Epic, ofType } from "redux-observable";
import * as localStore from "store/dist/store.modern";
import { RootState } from "../../store";
import * as authActions from "../auth/authActions";
import * as userActions from "./userActions";
import * as userSelectors from "./userSelectors";
import * as authSelectors from "../auth/authSelectors";
import { ServiceContainer } from "../../serviceContainer";
import { AnyAction } from "redux";
import { NEVER, from, of } from "rxjs";
import * as _ from "lodash";
import { catchError, map, mergeMap, switchMap, tap } from "rxjs/operators";

const hasMoreResults = (values: any) => {
  return values.length > 0;
};

const localStorageUserFiltersKey = "user:filters";

export const userEpic: Epic<any, any, RootState, ServiceContainer> = (
  action$,
  state$,
  services
) =>
  action$.pipe(
    ofType(userActions.GET_USER_LIST),
    mergeMap((action: userActions.GetUserListAction) => {
      const state = state$.value;

      let nextPageUrl: string | undefined;
      if (action.subtype === userActions.GetUserListSubtype.LoadNextPage) {
        nextPageUrl = userSelectors.nextPageUrl(state);
      }

      const filters = userSelectors.filters(state);

      return services.apiUser
        .users(
          authSelectors.accessToken(state),
          authSelectors.selectedOrganizationKeyName(state),
          nextPageUrl,
          filters
        )
        .pipe(
          mergeMap((x) =>
            of(
              userActions.getUserListSuccessAction(
                x.users.items,
                action.organization,
                x.users.links.next_page,
                x.users?.meta?.count || 0,
                hasMoreResults(x.users.items)
              )
            )
          ),
          catchError((e) => {
            services.toastr.error(
              "User list fetch fail",
              "There was error while fetching user list"
            );
            return of(userActions.getUserListFailAction(action.organization));
          })
        );
    })
  );

export const filterUsersEpic: Epic<any, any, RootState, ServiceContainer> = (
  action$,
  state$,
  services
) =>
  action$.pipe(
    ofType(userActions.SET_FILTERS_USER_LIST),
    tap((action: userActions.SetFiltersUserListAction) => {
      const currentFilters = localStore.get(localStorageUserFiltersKey);
      const newFilters = {
        ...currentFilters,
        [action.organization]: action.values,
      };
      localStore.set(localStorageUserFiltersKey, newFilters);
    }),
    switchMap((action: userActions.SetFiltersUserListAction) => {
      if (action.refreshList) {
        return of(
          userActions.getUserListAction(
            userActions.GetUserListSubtype.LoadFirstPage,
            action.organization
          )
        );
      }

      return NEVER;
    })
  );

export const resetUserFiltersEpic: Epic<
  any,
  any,
  RootState,
  ServiceContainer
> = (action$, state$, services) =>
  action$.pipe(
    ofType(userActions.RESET_FILTERS_USER_LIST),
    tap(() => localStore.remove(localStorageUserFiltersKey)),
    switchMap((action: userActions.ResetFiltersUserListAction) =>
      of(
        userActions.getUserListAction(
          userActions.GetUserListSubtype.LoadFirstPage,
          action.organization
        )
      )
    )
  );

export const denyUserEpic: Epic<any, any, RootState, ServiceContainer> = (
  action$,
  state$,
  services
) =>
  action$.pipe(
    ofType(userActions.DENY_USER),
    mergeMap((action: userActions.DenyUserAction) => {
      const state = state$.value;

      if (action.organization.length === 0) {
        return of(
          userActions.denyUserFailAction(action.organization, action.userId)
        );
      }

      return services.apiUser
        .denyUser(
          authSelectors.accessToken(state),
          action.organization,
          action.userId
        )
        .pipe(
          mergeMap((response) =>
            of<userActions.DenyUserSuccessAction>(
              userActions.denyUserSuccessAction(
                action.organization,
                action.userId
              )
            )
          ),
          tap(() =>
            services.toastr.success("User denied", "User has been denied")
          ),
          catchError(() => {
            services.toastr.error(
              "User not denied",
              "There was error while denying user"
            );
            return of(
              userActions.denyUserFailAction(action.organization, action.userId)
            );
          })
        );
    })
  );

export const approveUserEpic: Epic<any, any, RootState, ServiceContainer> = (
  action$,
  state$,
  services
) =>
  action$.pipe(
    ofType(userActions.APPROVE_USER),
    mergeMap((action: userActions.DenyUserAction) => {
      const state = state$.value;

      if (action.organization.length === 0) {
        return of(
          userActions.approveUserFailAction(action.organization, action.userId)
        );
      }

      return services.apiUser
        .approveUser(
          authSelectors.accessToken(state),
          action.organization,
          action.userId
        )
        .pipe(
          map(() =>
            userActions.approveUserSuccessAction(
              action.organization,
              action.userId
            )
          ),
          tap(() =>
            services.toastr.success("User approved", "User has been approved")
          ),
          catchError(() => {
            services.toastr.error(
              "User not approved",
              "There was error while approving user"
            );
            return of(
              userActions.approveUserFailAction(
                action.organization,
                action.userId
              )
            );
          })
        );
    })
  );

export const loadFiltersOnAppStartup: Epic<
  any,
  any,
  RootState,
  ServiceContainer
> = (action$, state$, services) =>
  action$.pipe(
    ofType(authActions.SELECT_ORGANIZATION_SUCCESS),
    switchMap((action: authActions.SelectOrganizationSuccessAction) => {
      const filters = localStore.get(localStorageUserFiltersKey);
      if (filters) {
        let actions: AnyAction[] = [];

        _.each(filters, (value, organization) => {
          actions.push(
            userActions.setFiltersUserListAction(organization, value, false)
          );
        });

        return from(actions);
      }

      return NEVER;
    })
  );

export const autocompleteUserEpic: Epic<
  any,
  any,
  RootState,
  ServiceContainer
> = (action$, state$, services) =>
  action$.pipe(
    ofType(userActions.AUTOCOMPLETE_USERS_POPUP),
    mergeMap((action: userActions.AutocompleteUsersPopupAction) => {
      const state = state$.value;

      return services.apiUser
        .autocompleteUsers(
          authSelectors.accessToken(state),
          action.organization,
          action.searchBy
        )
        .pipe(
          map((x) =>
            userActions.autocompleteUsersPopupSuccessAction(
              action.organization,
              x.userAutocompletes
            )
          ),
          catchError(() => {
            services.toastr.error(
              "User list fetch fail",
              "There was error while fetching user list"
            );
            return of(
              userActions.autocompleteUsersPopupFailAction(action.organization)
            );
          })
        );
    })
  );
